import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { DateTime } from "luxon";
import {
  PencilSquareIcon,
  TrashIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ChevronLeftIcon,
} from "@heroicons/react/20/solid";

import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import { useVenue } from "./VenueProvider";
import { showAlert } from "../alertService";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { generateCotizacionPDF } from "../utils/generateCotizacionPDF";
import { esCobrable } from "../utils/catalogo";
import CotizadorDesglose from "./cotizacion/CotizadorDesglose";
import {
  crearOLigarCliente,
  clienteDesdeLead,
  nombreCliente,
  camposEventoDesdeCliente,
} from "../utils/clientes";
import ClienteEventoDialog from "./directorio/ClienteEventoDialog";
import {
  COUNTRIES,
  FIELD_LABELS,
  formatDateOnly,
  formatDateRange,
  originColor,
  parsePhone,
  generateId,
  ORIGEN_SIN_ASIGNAR,
  leadConFechasVivas,
} from "../utils/leadHelpers";
import DateTimeDropdowns from "./DateTimeDropdowns";
import LeadNotasTimeline from "./LeadNotasTimeline";
import { esSoloLectura } from "../utils/roles";
import EspaciosPicker from "./shared/EspaciosPicker";
import {
  footprintDeEvento,
  footprintDesdeResumen,
  hayConflictoEntreFootprints,
  bloqueosPorEspacio,
} from "../utils/espacios";

const ACCENT = "#1b4d2e";
const VALID_TABS = ["general", "fechas", "timeline", "cotizacion"];

const LeadPage = () => {
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    selectedVenueId,
    selectedVenue,
    events: contextEvents,
    conceptos,
    venueFeatures,
    cotizacionConfig,
    espaciosActivos,
    espaciosPorId,
    permiteEventosSimultaneos,
    clientes,
  } = useVenue();
  const { userInfo, user } = useAuth();
  // isViewer decide qué se VE (el visor no ve notas ni la cotización);
  // soloLectura decide qué se ESCRIBE (visor e inversionista, P-FRA-13).
  const isViewer = userInfo?.role === "viewer";
  const soloLectura = esSoloLectura(userInfo);

  /* ── lead doc subscription ─────────────────────────────────── */
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  /* ── editing state ─────────────────────────────────────────── */
  const [editData, setEditData] = useState({});
  const [editSeeded, setEditSeeded] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  /* ── delete ────────────────────────────────────────────────── */
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  /* ── interest dates ────────────────────────────────────────── */
  // Default = UN día completo (00:00 → 23:59 del mismo día). Un lead pide una
  // fecha, no un rango que se derrama al día siguiente a la hora en que se
  // capturó — que es lo que daba `now` → `now + 1 día`.
  const FMT = "yyyy-LL-dd'T'HH:mm";
  const diaCompleto = (dt) => ({
    startDate: dt.startOf("day").toFormat(FMT),
    endDate: dt.endOf("day").toFormat(FMT),
  });
  const defaultDateRange = () => ({
    ...diaCompleto(DateTime.now()),
    label: "",
  });
  const [newDateRange, setNewDateRange] = useState(defaultDateRange);

  /* ── desglose / cotización ─────────────────────────────────── */
  const [exportingPDF, setExportingPDF] = useState(false);

  /* ── conversion dialog ─────────────────────────────────────── */
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  // Ligar o cambiar el cliente del lead (directorio).
  const [ligandoCliente, setLigandoCliente] = useState(false);
  const [convertDateIdx, setConvertDateIdx] = useState(0);
  // Espacios con los que se creará el evento. Se siembra del interés del lead
  // al abrir el diálogo — es la última oportunidad de decidirlo, porque el
  // evento ya creado solo se edita desde el calendario.
  const [convertEspacios, setConvertEspacios] = useState([]);
  const [convertCopyDesglose, setConvertCopyDesglose] = useState(true);
  const [converting, setConverting] = useState(false);

  /* ── all leads (for clash detection) ───────────────────────── */
  const [forms, setForms] = useState([]);

  /* ── existing origins for autocomplete ─────────────────────── */
  const existingOrigins = useMemo(() => {
    const set = new Set();
    forms.forEach((f) => {
      if (f.origin && f.origin.toLowerCase() !== "venueboard") set.add(f.origin);
    });
    return [...set].sort();
  }, [forms]);

  /* ── tab from URL ──────────────────────────────────────────── */
  const tab = (() => {
    const v = searchParams.get("tab");
    return VALID_TABS.includes(v) ? v : "general";
  })();
  const setTab = useCallback(
    (next) => {
      const sp = new URLSearchParams(searchParams);
      sp.set("tab", next);
      setSearchParams(sp, { replace: true, state: location.state });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams, location.state]
  );

  /* ── lead subscription ─────────────────────────────────────── */
  useEffect(() => {
    if (!leadId) return;
    const ref = doc(db, "venueContactForms", leadId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists() || snap.data()?.deleted) {
          setLead(null);
          setNotFound(true);
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setLead(data);
        setLoading(false);
        if (!editSeeded) {
          const parsed = parsePhone(data.phone);
          setEditData({
            name: data.name || "",
            phone: parsed.number,
            country: parsed.country,
            userEmail: data.userEmail || "",
            topic: data.topic || "",
            notes: data.notes || "",
            origin: data.origin || "",
            desglose: data.desglose || [],
            ivaEnabled: data.ivaEnabled || false,
          });
          setEditSeeded(true);
        } else {
          setEditData((prev) => ({
            ...prev,
            desglose: data.desglose || prev.desglose || [],
            ivaEnabled: data.ivaEnabled ?? prev.ivaEnabled ?? false,
          }));
        }
      },
      (err) => {
        console.error("Lead snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [leadId, editSeeded]);

  /* ── all-leads subscription (clash detection) ──────────────── */
  useEffect(() => {
    if (!selectedVenueId) {
      setForms([]);
      return;
    }
    const q = query(
      collection(db, "venueContactForms"),
      where("venueId", "==", selectedVenueId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setForms(
        snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((d) => !d.deleted)
      );
    });
    return unsub;
  }, [selectedVenueId]);

  /* ── ?delete=1 query opens delete confirm immediately ──────── */
  useEffect(() => {
    if (searchParams.get("delete") === "1" && lead && !lead.convertedToEventId && !soloLectura) {
      setConfirmingDelete(true);
    }
  }, [searchParams, lead, soloLectura]);

  /* ── derived ───────────────────────────────────────────────── */
  const isConverted = !!lead?.convertedToEventId;

  // Cliente del directorio ligado al lead. Al ligar se rellenan SOLO los
  // datos de contacto que el lead no tenía (los leads de relleno nacen sin
  // correo ni teléfono); lo capturado se respeta.
  const clienteDelLead = lead?.clienteId
    ? (clientes || []).find((c) => c.id === lead.clienteId) || null
    : null;
  const ligarClienteAlLead = async (clienteId, { cliente } = {}) => {
    if (!lead?.id || !clienteId) return;
    try {
      const ref = doc(db, "venueContactForms", lead.id);
      const patch = { clienteId, updatedAt: Timestamp.now() };
      const c = cliente || (clientes || []).find((x) => x.id === clienteId) || null;
      // Cambiar de cliente: los datos de contacto del lead son los del
      // cliente, así que se reemplazan; si no, el tablero seguía mostrando al
      // anterior. Ligar por primera vez un lead viejo solo completa lo vacío.
      const cambiaCliente = !!lead.clienteId && lead.clienteId !== clienteId;
      if (c && cambiaCliente) {
        patch.name = nombreCliente(c);
        patch.userEmail = c.correo || "";
        patch.phone = c.telefono || "";
      } else if (c) {
        if (!lead.name?.trim()) patch.name = nombreCliente(c);
        if (!lead.userEmail?.trim() && c.correo) patch.userEmail = c.correo;
        if (!lead.phone?.trim() && c.telefono) patch.phone = c.telefono;
      }
      await updateDoc(ref, patch);
      logBitacora(selectedVenueId, {
        action: BITACORA_ACTIONS.LEAD_EDIT,
        entity: "lead",
        entityId: lead.id,
        entityName: lead.name || "",
        details: {
          clienteId,
          clienteNombre: c ? nombreCliente(c) : "",
          ...(cambiaCliente
            ? { from: nombreCliente(clienteDelLead || {}), to: c ? nombreCliente(c) : "" }
            : {}),
        },
      }, userInfo, user);
      showAlert(cambiaCliente ? "Cliente del lead cambiado" : "Lead ligado al cliente", "success");
    } catch (err) {
      showAlert(`No se pudo ligar. [${err.message}]`, "error");
    }
  };
  const desglose = editData?.desglose || [];
  const desgloseTotal = desglose.reduce(
    (sum, item) => sum + (Number(item.units) * Number(item.unitPrice) || 0),
    0
  );
  const ivaEnabled = editData?.ivaEnabled || false;
  const totalConIva = desgloseTotal * 1.16;
  // Al cotizador solo entran los renglones cotizables (mismo criterio que
  // EventPage): ni archivados ni incluidos en la renta del venue.
  const conceptosCotizables = useMemo(
    () =>
      (conceptos || [])
        .filter((c) => !c.archivedAt && esCobrable(c))
        .sort((a, b) =>
          (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" })
        ),
    [conceptos]
  );
  const cotizacionHistory = lead?.cotizacionHistory || [];
  const interestDates = lead?.interestDates || [];
  // Espacios que le interesan al lead. Vacío ⇒ le sirve cualquiera (ver
  // getClashesForDate) y, al convertir, el evento bloquea todo el venue.
  const leadEspacios = useMemo(() => lead?.espaciosIds || [], [lead]);
  // Activos + los que el lead ya traía aunque hoy estén inactivos o borrados,
  // para que siempre se puedan quitar (mismo criterio que EventDialog).
  const opcionesEspaciosLead = useMemo(
    () => [
      ...espaciosActivos,
      ...leadEspacios
        .filter((id) => !espaciosActivos.some((e) => e.id === id))
        .map((id) => espaciosPorId[id] || { id, nombre: id }),
    ],
    [espaciosActivos, espaciosPorId, leadEspacios]
  );

  /* ── disponibilidad por espacio ────────────────────────────── */
  // Multiespacio cambia la pregunta: un evento en el Jardín no bloquea el
  // Salón. Y un LEAD no es un evento — mientras no declare qué espacio quiere,
  // le sirve CUALQUIERA, así que la fecha solo está perdida si no queda ni uno
  // libre; la ocupación parcial es informativa, no bloqueante. Ojo con la
  // asimetría deliberada: en un EVENTO la ausencia de footprint significa lo
  // contrario (ocupa todo el venue). Ver utils/espacios.js.
  const manejaEspacios = espaciosActivos.length > 0 && permiteEventosSimultaneos;
  const nombreEspacio = useCallback(
    (id) => espaciosPorId[id]?.nombre || id,
    [espaciosPorId]
  );

  const getClashesForDate = useCallback(
    (startDate, endDate, currentLeadId, linkedEventId, espaciosInteres) => {
      const clashes = [];
      const start = startDate instanceof Date ? startDate : new Date(startDate);
      const end = endDate instanceof Date ? endDate : new Date(endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return clashes;

      // Eventos que cruzan el rango en el tiempo — la primera de las dos
      // etapas del predicado. La segunda (footprint) solo aplica si el venue
      // maneja espacios.
      const cruzan = [];
      (contextEvents || []).forEach((e) => {
        if (e.status === "cancelado") return;
        if (linkedEventId && e.id === linkedEventId) return;
        const eStart = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
        const eEnd = e.endTime?.toDate ? e.endTime.toDate() : new Date(e.endTime);
        if (isNaN(eStart?.getTime()) || isNaN(eEnd?.getTime())) return;
        if (start < eEnd && end > eStart) cruzan.push({ e, eStart, eEnd });
      });

      const comoConflicto = ({ e, eStart, eEnd }, espacios) => ({
        type: "event",
        name: e.nombre || e.title || "Evento",
        severity: "red",
        eventStart: eStart,
        eventEnd: eEnd,
        espacios: espacios || [],
      });

      const seleccion = espaciosInteres || [];
      let miFpLead = null; // footprint del interés, para el cruce lead-lead

      if (!manejaEspacios) {
        cruzan.forEach((c) => clashes.push(comoConflicto(c)));
      } else if (seleccion.length > 0) {
        // Interés declarado: solo es conflicto lo que toca esos espacios.
        miFpLead = footprintDesdeResumen(seleccion, espaciosPorId);
        const misEspacios = seleccion
          .map((id) => espaciosPorId[id])
          .filter(Boolean);
        cruzan.forEach((c) => {
          const suFp = footprintDeEvento(c.e);
          if (!hayConflictoEntreFootprints(miFpLead, suFp, espaciosPorId)) return;
          // Qué espacios DE LOS QUE PIDE quedan tomados por este evento.
          const tomados = [...bloqueosPorEspacio([{ fp: suFp, ref: c.e }], misEspacios).keys()];
          clashes.push(comoConflicto(c, tomados.map(nombreEspacio)));
        });
      } else {
        // Sin interés declarado: la fecha se pierde solo si no queda espacio.
        const bloqueos = bloqueosPorEspacio(
          cruzan.map((c) => ({ fp: footprintDeEvento(c.e), ref: c.e })),
          espaciosActivos
        );
        const libres = espaciosActivos.filter((esp) => !bloqueos.has(esp.id));
        if (bloqueos.size > 0 && libres.length === 0) {
          cruzan.forEach((c) => clashes.push(comoConflicto(c)));
        } else if (bloqueos.size > 0) {
          clashes.push({
            type: "ocupacion",
            severity: "amber",
            ocupados: bloqueos.size,
            total: espaciosActivos.length,
            libres: libres.map((e) => e.nombre),
          });
        }
      }

      // Otros leads interesados en la misma fecha. Con espacios declarados de
      // ambos lados, dos leads que piden salones distintos no se estorban.
      const otherLeadNames = new Set();
      forms.forEach((f) => {
        if (f.id === currentLeadId) return;
        // Un lead perdido, convertido o eliminado ya no aparta nada.
        if (!leadConFechasVivas(f)) return;
        const solapaEnEspacio =
          !manejaEspacios ||
          hayConflictoEntreFootprints(
            miFpLead,
            footprintDesdeResumen(f.espaciosIds || [], espaciosPorId),
            espaciosPorId
          );
        if (!solapaEnEspacio) return;
        (f.interestDates || []).forEach((d) => {
          const dStart = d.startDate?.toDate ? d.startDate.toDate() : new Date(d.startDate);
          const dEnd = d.endDate?.toDate ? d.endDate.toDate() : new Date(d.endDate);
          if (isNaN(dStart?.getTime()) || isNaN(dEnd?.getTime())) return;
          if (start < dEnd && end > dStart)
            otherLeadNames.add(f.name || f.topic || "Lead");
        });
      });
      if (otherLeadNames.size > 0) {
        clashes.push({ type: "lead", names: [...otherLeadNames], severity: "amber" });
      }
      return clashes;
    },
    [contextEvents, forms, manejaEspacios, espaciosActivos, espaciosPorId, nombreEspacio]
  );

  /* ── handlers ──────────────────────────────────────────────── */
  // Board params carried into this URL by Formularios — send them back so the
  // pipeline restores search, filters, sort and tab.
  const boardSearch = (() => {
    const sp = new URLSearchParams();
    ["view", "q", "origen", "fecha", "orden"].forEach((k) => {
      const v = searchParams.get(k);
      if (v) sp.set(k, v);
    });
    const qs = sp.toString();
    return qs ? `?${qs}` : "";
  })();

  const handleBack = () => {
    navigate(`/dashboard/leads${boardSearch}`, {
      state: { fromLeadId: leadId, tab: location.state?.fromTab },
    });
  };

  const handleSaveLead = async () => {
    if (!lead) return;
    setSavingEdit(true);
    try {
      const ref = doc(db, "venueContactForms", lead.id);
      const updates = {};
      const changes = [];
      const phoneStr = editData.phone?.trim()
        ? `${COUNTRIES.find((c) => c.code === editData.country)?.prefix || "+52"} ${editData.phone.trim()}`
        : "";

      const originLocked =
        !lead.origin || lead.origin.toLowerCase() === "venueboard";

      // Con cliente ligado, nombre, correo y teléfono son del cliente y no
      // se tocan desde aquí (la UI tampoco los ofrece).
      const conCliente = !!lead.clienteId;
      const fieldMap = {
        ...(conCliente
          ? {}
          : {
              name: editData.name?.trim() || "",
              userEmail: editData.userEmail?.trim() || "",
            }),
        topic: editData.topic?.trim() || "",
        notes: editData.notes?.trim() || "",
        ...(originLocked
          ? {}
          : { origin: editData.origin?.trim() || ORIGEN_SIN_ASIGNAR }),
      };

      for (const [key, newVal] of Object.entries(fieldMap)) {
        const oldVal = lead[key] || "";
        if (oldVal !== newVal) {
          updates[key] = newVal;
          changes.push({
            field: FIELD_LABELS[key] || key,
            from: oldVal || "-",
            to: newVal || "-",
          });
        }
      }

      const oldPhone = lead.phone || "";
      if (!conCliente && oldPhone !== phoneStr) {
        updates.phone = phoneStr;
        updates.country = editData.country;
        changes.push({ field: "Teléfono", from: oldPhone || "-", to: phoneStr || "-" });
      }

      if (Object.keys(updates).length === 0) {
        showAlert("Sin cambios", "info");
        setSavingEdit(false);
        return;
      }

      updates.updatedAt = Timestamp.now();
      await updateDoc(ref, updates);
      showAlert("Lead actualizado", "success");
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_EDIT,
          entity: "lead",
          entityId: lead.id,
          entityName: editData.name?.trim() || lead?.topic || "",
          details: changes.length > 0 ? { changes } : {},
        },
        userInfo,
        user
      );
    } catch (err) {
      console.error(err);
      showAlert("Error actualizando lead", "error");
    }
    setSavingEdit(false);
  };

  const handleDeleteLead = async () => {
    if (!lead) return;
    try {
      const ref = doc(db, "venueContactForms", lead.id);
      await updateDoc(ref, { deleted: true, deleteReason: deleteReason || "" });
      showAlert("Lead eliminado", "success");
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_DELETE,
          entity: "lead",
          entityId: lead.id,
          entityName: lead?.name || lead?.topic || "",
          details: { reason: deleteReason || "" },
        },
        userInfo,
        user
      );
      navigate(`/dashboard/leads${boardSearch}`, {
        state: { fromLeadId: lead.id },
      });
    } catch {
      showAlert("Error eliminando lead", "error");
    }
  };

  /* ── espacios de interés ───────────────────────────────────── */
  // Escribe directo, igual que las fechas de interés: esta pestaña no pasa
  // por la barra de guardado de la pestaña General.
  const handleSetEspaciosInteres = async (ids) => {
    if (!lead) return;
    try {
      const ref = doc(db, "venueContactForms", lead.id);
      await updateDoc(ref, { espaciosIds: ids, updatedAt: Timestamp.now() });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_EDIT,
          entity: "lead",
          entityId: lead.id,
          entityName: lead?.name || lead?.topic || "",
          details: {
            changes: [
              {
                field: "Espacios de interés",
                from: leadEspacios.map(nombreEspacio).join(", ") || "-",
                to: ids.map(nombreEspacio).join(", ") || "-",
              },
            ],
          },
        },
        userInfo,
        user
      );
    } catch (err) {
      console.error(err);
      showAlert("Error actualizando espacios", "error");
    }
  };

  /* ── interest dates ────────────────────────────────────────── */
  const handleAddInterestDate = async () => {
    if (!lead) return;
    if (!newDateRange.startDate || !newDateRange.endDate) {
      showAlert("Fecha de inicio y fin requeridas", "error");
      return;
    }
    const start = new Date(newDateRange.startDate);
    const end = new Date(newDateRange.endDate);
    if (end <= start) {
      showAlert("La fecha de fin debe ser posterior al inicio", "error");
      return;
    }
    try {
      const ref = doc(db, "venueContactForms", lead.id);
      const newDate = {
        id: generateId(),
        startDate: Timestamp.fromDate(start),
        endDate: Timestamp.fromDate(end),
        label: newDateRange.label.trim(),
      };
      const current = lead.interestDates || [];
      const updated = [...current, newDate];
      await updateDoc(ref, { interestDates: updated, updatedAt: Timestamp.now() });
      setNewDateRange(defaultDateRange());
      showAlert("Fecha de interés agregada", "success");
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_ADD_DATE,
          entity: "lead",
          entityId: lead.id,
          entityName: lead?.name || lead?.topic || "",
          details: { label: newDate.label || formatDateRange(start, end) },
        },
        userInfo,
        user
      );
    } catch {
      showAlert("Error agregando fecha", "error");
    }
  };

  const handleRemoveInterestDate = async (dateId) => {
    if (!lead) return;
    try {
      const ref = doc(db, "venueContactForms", lead.id);
      const current = lead.interestDates || [];
      const removed = current.find((d) => d.id === dateId);
      const updated = current.filter((d) => d.id !== dateId);
      await updateDoc(ref, { interestDates: updated, updatedAt: Timestamp.now() });
      showAlert("Fecha eliminada", "success");
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_REMOVE_DATE,
          entity: "lead",
          entityId: lead.id,
          entityName: lead?.name || lead?.topic || "",
          details: { label: removed?.label || "" },
        },
        userInfo,
        user
      );
    } catch {
      showAlert("Error eliminando fecha", "error");
    }
  };

  /* ── desglose ──────────────────────────────────────────────── */
  const saveDesgloseToDB = async (newDesglose) => {
    if (!lead) return;
    const ref = doc(db, "venueContactForms", lead.id);
    await updateDoc(ref, { desglose: newDesglose, updatedAt: Timestamp.now() });
    logBitacora(
      selectedVenueId,
      {
        action: BITACORA_ACTIONS.LEAD_EDIT_DESGLOSE,
        entity: "lead",
        entityId: lead.id,
        entityName: lead?.name || lead?.topic || "",
        details: {},
      },
      userInfo,
      user
    );
  };

  const saveIvaEnabled = async (val) => {
    if (!lead) return;
    const ref = doc(db, "venueContactForms", lead.id);
    await updateDoc(ref, { ivaEnabled: val, updatedAt: Timestamp.now() });
    setEditData((prev) => ({ ...prev, ivaEnabled: val }));
    logBitacora(
      selectedVenueId,
      {
        action: BITACORA_ACTIONS.LEAD_TOGGLE_IVA,
        entity: "lead",
        entityId: lead.id,
        entityName: lead?.name || lead?.topic || "",
        details: { ivaEnabled: val },
      },
      userInfo,
      user
    );
  };

  /* ── cotización PDF ────────────────────────────────────────── */
  const isPremiumCotizaciones = venueFeatures?.premiumCotizaciones === true;

  const handleExportLeadPDF = async () => {
    // Solo lectura no genera PDFs (P-FRA-13): el botón no se muestra.
    if (exportingPDF || !lead || soloLectura) return;
    setExportingPDF(true);
    try {
      const historyCount = (lead?.cotizacionHistory || []).length + 1;
      const folio = `COT-L-${String(historyCount).padStart(4, "0")}`;

      const resolvedConfig = isPremiumCotizaciones
        ? cotizacionConfig
        : {
            primaryColor: "#1b4d2e",
            accentColor: "#1b4d2e",
            secondaryColor: "#f0fdf4",
            logoUrl: "",
            logoSize: 60,
            headerHeight: 80,
            showVenueName: true,
            direccion: "",
            telefono: "",
            correo: "",
            vigencia: "30 días",
            leyendaExtra: "",
            nombreCotizacion: cotizacionConfig?.nombreCotizacion || "",
          };

      const firstDate = (lead.interestDates || [])[0];
      // Con cliente, la cotización sale con sus datos (apellido, empresa,
      // RFC); los campos sueltos del lead solo cuentan en leads viejos.
      const datosCliente = clienteDelLead
        ? camposEventoDesdeCliente(clienteDelLead)
        : { cliente: lead.name || "", correo: lead.userEmail || "", telefono: lead.phone || "" };
      const eventLike = {
        ...datosCliente,
        nombre: lead.topic || "Lead",
        title: lead.topic || "Lead",
        start: firstDate?.startDate || null,
        end: firstDate?.endDate || null,
      };

      await generateCotizacionPDF({
        venue: selectedVenue,
        event: eventLike,
        desglose,
        ivaEnabled,
        folio,
        isPremium: isPremiumCotizaciones,
        cotizacionConfig: resolvedConfig,
      });

      const historyEntry = {
        generatedAt: Timestamp.now(),
        generatedBy: user?.uid || "",
        generatedByName: userInfo?.name || "",
        folio,
        sent: false,
        sentAt: null,
        sentBy: null,
      };
      const ref = doc(db, "venueContactForms", lead.id);
      const currentHistory = lead.cotizacionHistory || [];
      await updateDoc(ref, {
        cotizacionHistory: [...currentHistory, historyEntry],
        updatedAt: Timestamp.now(),
      });

      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_COTIZACION_GENERATE,
          entity: "lead",
          entityId: lead.id,
          entityName: lead?.name || lead?.topic || "",
          details: {
            folio,
            desglose: desglose.map((item) => ({
              concepto: item.concept || "-",
              unidades: item.units,
              precioUnitario: item.unitPrice,
              subtotal: Number(item.units || 0) * Number(item.unitPrice || 0),
            })),
            subtotal: desgloseTotal,
            iva: ivaEnabled ? desgloseTotal * 0.16 : 0,
            total: ivaEnabled ? totalConIva : desgloseTotal,
          },
        },
        userInfo,
        user
      );

      showAlert("Cotización PDF generada", "success");
    } catch (err) {
      console.error("PDF generation error:", err);
      showAlert("Error generando PDF", "error");
    }
    setExportingPDF(false);
  };

  const handleToggleCotizacionSent = async (idx) => {
    if (!lead) return;
    const ref = doc(db, "venueContactForms", lead.id);
    const history = [...(lead?.cotizacionHistory || [])];
    if (!history[idx]) return;
    const wasSent = history[idx].sent;
    history[idx] = {
      ...history[idx],
      sent: !wasSent,
      sentAt: !wasSent ? Timestamp.now() : null,
      sentBy: !wasSent ? user?.uid || "" : null,
    };
    await updateDoc(ref, { cotizacionHistory: history });
  };

  /* ── conversion ────────────────────────────────────────────── */
  const handleConvertToEvent = async () => {
    if (converting || !lead) return;
    // El evento y la ficha del cliente se crean bajo el venue SELECCIONADO,
    // no bajo el del lead. Con un link viejo y otro venue activo (owner
    // multi-venue o admin) el evento caía en el venue equivocado y el lead
    // quedaba con un `clienteId` que apunta a otro venue.
    if (lead.venueId && lead.venueId !== selectedVenueId) {
      showAlert(
        "Este lead es de otro venue. Cambia al venue del lead para convertirlo en evento.",
        "error"
      );
      return;
    }
    setConverting(true);
    try {
      const dates = lead.interestDates || [];
      const selectedDate = dates[convertDateIdx] || dates[0];
      if (!selectedDate) {
        showAlert("Se necesita al menos una fecha de interés", "error");
        setConverting(false);
        return;
      }

      const startDate = selectedDate.startDate?.toDate
        ? selectedDate.startDate.toDate()
        : new Date(selectedDate.startDate);
      const endDate = selectedDate.endDate?.toDate
        ? selectedDate.endDate.toDate()
        : new Date(selectedDate.endDate);

      const eventData = {
        eventType: "event",
        nombre: lead.topic || "Evento",
        cliente: lead.name || "",
        correo: lead.userEmail || "",
        telefono: lead.phone || "",
        startTime: Timestamp.fromDate(startDate),
        endTime: Timestamp.fromDate(endDate),
        inicioMontaje: Timestamp.fromDate(startDate),
        finMontaje: Timestamp.fromDate(endDate),
        inicioEvento: Timestamp.fromDate(startDate),
        finEvento: Timestamp.fromDate(endDate),
        status: "vigente",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        precioAcordado: 0,
        totalPagado: 0,
        pagado: false,
        convertedFromLeadId: lead.id,
      };

      // Footprint congelado al crear, igual que en el calendario. Sin esto el
      // evento nace sin `unidadesOcupadas`, que todo consumidor —calendario,
      // conflictos y la disponibilidad pública del homesite— lee como "ocupa
      // TODO el venue": un lead de un salón bloquearía el venue entero.
      if (espaciosActivos.length > 0 && convertEspacios.length > 0) {
        const footprint = footprintDesdeResumen(convertEspacios, espaciosPorId);
        eventData.espaciosIds = convertEspacios;
        eventData.unidadesOcupadas = footprint ? [...footprint].sort() : [];
      }

      if (convertCopyDesglose && lead.desglose?.length > 0) {
        eventData.desglose = lead.desglose;
        eventData.ivaEnabled = lead.ivaEnabled || false;
        const total = lead.desglose.reduce(
          (s, i) => s + (Number(i.units) * Number(i.unitPrice) || 0),
          0
        );
        eventData.precioAcordado = lead.ivaEnabled ? total * 1.16 : total;
      }

      // La conversión crea o liga la ficha del cliente (FRA-354): el lead
      // que ya traía `clienteId` (generado desde una ficha) la reutiliza;
      // si no, se busca por correo o teléfono y, si no existe, se crea.
      let clienteId = lead.clienteId || null;
      if (!clienteId) {
        try {
          const r = await crearOLigarCliente({
            venueId: selectedVenueId,
            clientes,
            datos: clienteDesdeLead(lead),
            user,
            origen: "lead",
          });
          clienteId = r.clienteId;
        } catch (err) {
          console.warn("No se pudo crear la ficha del cliente:", err);
        }
      }
      // El evento toma los datos del cliente del directorio, no los sueltos
      // del lead: el lead guarda el nombre completo en un solo campo y no
      // trae apellido, empresa ni RFC, así que el evento nacía con el aviso
      // «difieren del cliente» y la cotización sin RFC. Si el cliente se
      // acaba de crear y aún no está en el contexto, se quedan los del lead
      // (de ellos nació).
      if (clienteId) {
        const ficha = (clientes || []).find((c) => c.id === clienteId);
        if (ficha) Object.assign(eventData, camposEventoDesdeCliente(ficha));
        else eventData.clienteId = clienteId;
      }

      const eventsCol = collection(
        db,
        "venues",
        selectedVenueId,
        "calendars",
        "default",
        "events"
      );
      const eventRef = await addDoc(eventsCol, eventData);

      const leadRef = doc(db, "venueContactForms", lead.id);
      await updateDoc(leadRef, {
        status: "ganado",
        ...(clienteId ? { clienteId } : {}),
        // El lead queda con los espacios que realmente se reservaron.
        ...(espaciosActivos.length > 0 ? { espaciosIds: convertEspacios } : {}),
        convertedToEventId: eventRef.id,
        convertedAt: Timestamp.now(),
        convertedBy: user?.uid || "",
        updatedAt: Timestamp.now(),
      });

      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.LEAD_CONVERT,
          entity: "lead",
          entityId: lead.id,
          entityName: lead?.name || lead?.topic || "",
          details: {
            eventId: eventRef.id,
            eventName: eventData.nombre,
            ...(espaciosActivos.length > 0
              ? { espacios: convertEspacios.map(nombreEspacio) }
              : {}),
          },
        },
        userInfo,
        user
      );

      showAlert("Lead convertido a evento", "success");
      setShowConvertDialog(false);
      navigate(`/dashboard/eventos/${eventRef.id}`);
    } catch (err) {
      console.error("Conversion error:", err);
      showAlert("Error convirtiendo lead", "error");
    }
    setConverting(false);
  };

  /* ── render guards ─────────────────────────────────────────── */
  if (loading) {
    return <div className="p-8 text-center text-zinc-500">Cargando lead...</div>;
  }
  if (notFound) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center text-zinc-500">
        <div className="mb-4">No se encontró el lead.</div>
        <button
          onClick={() => navigate(`/dashboard/leads${boardSearch}`)}
          className="inline-flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900"
        >
          ← Volver a leads
        </button>
      </div>
    );
  }
  if (!lead) return null;

  const linkedEvent = isConverted
    ? (contextEvents || []).find((e) => e.id === lead.convertedToEventId)
    : null;
  const eventIsCancelled = linkedEvent?.status === "cancelado";

  return (
    <div className="max-w-6xl mx-auto pb-32">
      {/* Back button */}
      <button
        onClick={handleBack}
        className="inline-flex items-center gap-1 text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition mb-4"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Volver a leads
      </button>

      {/* Converted banner — prominent */}
      {isConverted && (
        <div
          className={`rounded-xl border-2 p-5 shadow-sm mb-6 ${
            eventIsCancelled
              ? "border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/10"
              : "border-green-300 dark:border-green-700/60 bg-green-50 dark:bg-green-900/10"
          }`}
        >
          <div className="flex items-start gap-3">
            {eventIsCancelled ? (
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500 shrink-0 mt-0.5" />
            ) : (
              <CheckCircleIcon className="h-6 w-6 text-green-600 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div
                className={`text-base font-semibold ${
                  eventIsCancelled
                    ? "text-red-800 dark:text-red-300"
                    : "text-green-800 dark:text-green-300"
                }`}
              >
                {eventIsCancelled
                  ? "Este lead se convirtió en un evento que ya fue cancelado"
                  : "Este lead ya es un evento"}
              </div>
              <div className="text-sm text-zinc-600 dark:text-zinc-300 mt-1">
                Los cambios y nuevos seguimientos se hacen desde el evento.
              </div>
            </div>
            <button
              onClick={() =>
                navigate(`/dashboard/eventos/${lead.convertedToEventId}`)
              }
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 ${
                eventIsCancelled ? "bg-red-600" : ""
              }`}
              style={eventIsCancelled ? {} : { backgroundColor: ACCENT }}
            >
              Ir al evento
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header card */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-6 shadow-sm mb-6">
        <div className="flex items-start gap-4">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: ACCENT + "1a" }}
          >
            <PencilSquareIcon className="h-5 w-5" style={{ color: ACCENT }} />
          </div>
          <div className="min-w-0 flex-1">
            {/* El nombre del lead se edita aquí, donde se lee, no en un
                campo perdido entre los datos de contacto. La página ya
                guarda con "Guardar cambios", así que es el mismo flujo. */}
            {isConverted || soloLectura ? (
              <h1
                className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white"
                style={{ hyphens: "auto", overflowWrap: "break-word" }}
                lang="es"
              >
                {lead.topic || <span className="italic text-zinc-400">Sin nombre</span>}
              </h1>
            ) : (
              <input
                type="text"
                value={editData.topic ?? lead.topic ?? ""}
                onChange={(e) => setEditData((p) => ({ ...p, topic: e.target.value }))}
                placeholder="Nombre del lead"
                aria-label="Nombre del lead"
                className="w-full -ml-2 px-2 py-0.5 rounded-lg border border-transparent bg-transparent text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white placeholder:text-zinc-300 placeholder:font-normal hover:border-zinc-200 dark:hover:border-zinc-700 focus:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]/30"
              />
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="text-xs text-zinc-400">
                Creado el{" "}
                {lead.createdAt?.toDate
                  ? formatDateOnly(lead.createdAt.toDate())
                  : "-"}
              </span>
              <span className="text-xs text-zinc-300">·</span>
              <span className="text-xs text-zinc-400">Origen:</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded font-medium ${originColor(lead.origin)}`}
              >
                {lead.origin || "Venueboard"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 dark:border-zinc-700 mb-6">
        <nav className="-mb-px flex gap-2 overflow-x-auto">
          {[
            { id: "general", label: "General" },
            { id: "fechas", label: "Fechas" },
            { id: "timeline", label: "Timeline" },
            ...(isViewer ? [] : [{ id: "cotizacion", label: "Cotización" }]),
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? "border-[#1b4d2e] text-[#1b4d2e]"
                  : "border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300 dark:text-zinc-400 dark:hover:text-zinc-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ── GENERAL TAB ─────────────────────────────────────── */}
      {tab === "general" && (
        <div className="space-y-6">
          {/* Información de contacto */}
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: ACCENT + "1a" }}
              >
                <PencilSquareIcon className="h-4 w-4" style={{ color: ACCENT }} />
              </div>
              <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Información de contacto
              </span>
              {/* Cliente del directorio: ver, cambiar o ligar. Es lo que
                  permite saber si esta persona ya cotizó antes. */}
              <span className="ml-auto inline-flex items-center gap-3 text-xs">
                {lead.clienteId ? (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/dashboard/clientes/${lead.clienteId}`)
                      }
                      className="font-semibold hover:underline"
                      style={{ color: ACCENT }}
                    >
                      Ver cliente{clienteDelLead ? `: ${nombreCliente(clienteDelLead)}` : ""} →
                    </button>
                    {!soloLectura && !isConverted && (
                      <button
                        type="button"
                        onClick={() => setLigandoCliente(true)}
                        className="font-medium text-zinc-500 hover:text-zinc-700 hover:underline"
                      >
                        Cambiar cliente
                      </button>
                    )}
                  </>
                ) : (
                  !soloLectura && (
                    <button
                      type="button"
                      onClick={() => setLigandoCliente(true)}
                      className="font-semibold hover:underline"
                      style={{ color: ACCENT }}
                    >
                      Ligar a un cliente
                    </button>
                  )
                )}
              </span>
            </div>
            <ClienteEventoDialog
              open={ligandoCliente}
              datosDelEvento={clienteDesdeLead(lead)}
              excluir={[lead.clienteId]}
              titulo={lead.clienteId ? "Cambiar el cliente del lead" : "Cliente del lead"}
              subtitulo={
                lead.clienteId
                  ? `Hoy está ligado a ${clienteDelLead ? nombreCliente(clienteDelLead) : "otro cliente"}. Elige quién lo reemplaza.`
                  : "Búscalo en el directorio o dalo de alta"
              }
              textoCrear="Crear y ligar al lead"
              origenCreacion="lead"
              onClose={() => setLigandoCliente(false)}
              onLigar={(clienteId, { cliente } = {}) => {
                setLigandoCliente(false);
                ligarClienteAlLead(clienteId, { cliente });
              }}
            />
            {isConverted ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-xs font-medium text-zinc-400 mb-0.5">Nombre</span>
                  <span className="text-sm text-zinc-800 dark:text-zinc-200">{lead.name || "-"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-zinc-400 mb-0.5">Teléfono</span>
                  <span className="text-sm text-zinc-800 dark:text-zinc-200">{lead.phone || "-"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-zinc-400 mb-0.5">Email</span>
                  <span className="text-sm text-zinc-800 dark:text-zinc-200">{lead.userEmail || "-"}</span>
                </div>
                <div>
                  <span className="block text-xs font-medium text-zinc-400 mb-0.5">Tema</span>
                  <span className="text-sm text-zinc-800 dark:text-zinc-200">{lead.topic || "-"}</span>
                </div>
                {lead.notes && (
                  <div className="col-span-2">
                    <span className="block text-xs font-medium text-zinc-400 mb-0.5">Notas</span>
                    <span className="text-sm text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap">{lead.notes}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Con cliente ligado, nombre, teléfono y correo son del
                    cliente y se leen de su ficha: aquí no se editan, se
                    corrigen allá (o se cambia de cliente). Sin cliente, los
                    campos sueltos del lead siguen editables. */}
                {lead.clienteId ? (
                  <div className="flex flex-wrap gap-x-8 gap-y-2 rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 px-4 py-3">
                    {[
                      ["Nombre", clienteDelLead ? nombreCliente(clienteDelLead) : lead.name],
                      ["Teléfono", clienteDelLead?.telefono || lead.phone],
                      ["Email", clienteDelLead?.correo || lead.userEmail],
                    ].map(([label, valor]) => (
                      <div key={label} className="min-w-0">
                        <div className="text-xs font-medium text-zinc-400">{label}</div>
                        <div className="text-sm text-zinc-800 dark:text-zinc-200 break-words">
                          {valor || <span className="text-zinc-400">—</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Nombre</label>
                    <Input
                      value={editData.name || ""}
                      onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Nombre del contacto"
                      disabled={soloLectura}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Teléfono</label>
                    <div className="flex items-center rounded-lg border border-zinc-950/10 bg-white shadow-sm dark:border-white/10 dark:bg-white/5 focus-within:ring-2 focus-within:ring-blue-500">
                      <select
                        value={editData.country || "MX"}
                        onChange={(e) => setEditData((p) => ({ ...p, country: e.target.value }))}
                        className="shrink-0 border-none bg-transparent pl-3 pr-1 py-2 text-sm text-zinc-700 focus:outline-none dark:text-zinc-200"
                        disabled={soloLectura}
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code === "MX" ? "\u{1F1F2}\u{1F1FD} +52" : "\u{1F1FA}\u{1F1F8} +1"}
                          </option>
                        ))}
                      </select>
                      <span className="text-zinc-300 dark:text-zinc-600">|</span>
                      <input
                        value={editData.phone || ""}
                        onChange={(e) => setEditData((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="Número de teléfono"
                        className="flex-1 border-none bg-transparent px-2 py-2 text-sm text-zinc-950 placeholder:text-zinc-500 focus:outline-none dark:text-white"
                        disabled={soloLectura}
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Email</label>
                    <Input
                      type="email"
                      value={editData.userEmail || ""}
                      onChange={(e) => setEditData((p) => ({ ...p, userEmail: e.target.value }))}
                      placeholder="correo@ejemplo.com"
                      disabled={soloLectura}
                    />
                  </div>
                </div>
                </>
                )}

                {/* Notas (internas, ocultas para visores) */}
                {!isViewer && (
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Notas</label>
                    <Textarea
                      value={editData.notes || ""}
                      onChange={(e) => setEditData((p) => ({ ...p, notes: e.target.value }))}
                      placeholder="Notas internas sobre este lead..."
                      rows={3}
                      disabled={soloLectura}
                    />
                  </div>
                )}

                {/* Message (read-only, from website forms) */}
                {lead.message && (
                  <div>
                    <span className="block text-xs font-medium text-zinc-400 mb-1">Mensaje original</span>
                    <div className="rounded-lg bg-zinc-50 dark:bg-zinc-700/30 p-3 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
                      {lead.message}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Origen</label>
                  {(() => {
                    const originLocked =
                      !lead?.origin || lead.origin.toLowerCase() === "venueboard";
                    return (
                      <>
                        <Input
                          list="origin-suggestions-edit"
                          value={originLocked ? "Venueboard" : editData.origin || ""}
                          onChange={(e) =>
                            setEditData((p) => ({ ...p, origin: e.target.value }))
                          }
                          placeholder="Instagram, Referido, Llamada..."
                          disabled={soloLectura || originLocked}
                        />
                        {originLocked && (
                          <p className="mt-1 text-xs text-zinc-400">
                            Este lead vino del formulario público — el origen no se puede cambiar.
                          </p>
                        )}
                        <datalist id="origin-suggestions-edit">
                          {existingOrigins.map((o) => (
                            <option key={o} value={o} />
                          ))}
                        </datalist>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── FECHAS TAB ──────────────────────────────────────── */}
      {tab === "fechas" && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <CalendarDaysIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Fechas de interés
            </span>
          </div>

          {/* Espacios de interés — decide qué eventos cuentan como conflicto
              en las fechas de abajo y con qué footprint nace el evento al
              convertir. Solo si el venue maneja espacios. */}
          {espaciosActivos.length > 0 && (
            <div className="mb-5 rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2">
                Espacios de interés
              </div>
              {isConverted ? (
                <div className="flex flex-wrap gap-1.5">
                  {leadEspacios.length > 0 ? (
                    leadEspacios.map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
                      >
                        {nombreEspacio(id)}
                      </span>
                    ))
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-zinc-200/70 dark:bg-zinc-700 px-2.5 py-0.5 text-xs font-medium text-zinc-600 dark:text-zinc-300">
                      Todo el venue
                    </span>
                  )}
                </div>
              ) : (
                <>
                  <EspaciosPicker
                    opciones={opcionesEspaciosLead}
                    value={leadEspacios}
                    onChange={handleSetEspaciosInteres}
                    disabled={soloLectura}
                    size="sm"
                  />
                  <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                    {leadEspacios.length === 0
                      ? "Sin espacios seleccionados: se asume que le sirve cualquiera, y una fecha solo se marca no disponible cuando ningún espacio queda libre."
                      : "Las fechas de abajo se evalúan contra estos espacios — un evento en otro salón deja de contar como conflicto."}
                  </p>
                </>
              )}
            </div>
          )}

          {isConverted &&
            (() => {
              if (!linkedEvent) return null;
              const evStart = linkedEvent.startTime?.toDate
                ? linkedEvent.startTime.toDate()
                : new Date(linkedEvent.startTime);
              const evEnd = linkedEvent.endTime?.toDate
                ? linkedEvent.endTime.toDate()
                : new Date(linkedEvent.endTime);
              if (isNaN(evStart?.getTime()) || isNaN(evEnd?.getTime())) return null;
              return (
                <div className="mb-4 rounded-lg border border-green-300 dark:border-green-700/60 bg-green-50 dark:bg-green-900/10 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-400 mb-1.5">
                    <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />
                    Fecha del evento
                  </div>
                  <div className="text-sm font-medium text-green-900 dark:text-green-200">
                    {formatDateRange(evStart, evEnd)}
                  </div>
                </div>
              );
            })()}

          {isConverted && interestDates.length > 0 && (
            <div className="text-xs font-medium text-zinc-400 dark:text-zinc-500 mb-2">
              Fechas de interés originales
            </div>
          )}

          {interestDates.length === 0 && !isConverted && (
            <p className="text-sm text-zinc-400 italic mb-3">
              Sin fechas de interés registradas.
            </p>
          )}

          {interestDates.map((d) => {
            const start = d.startDate?.toDate ? d.startDate.toDate() : new Date(d.startDate);
            const end = d.endDate?.toDate ? d.endDate.toDate() : new Date(d.endDate);
            const clashes = isConverted
              ? []
              : getClashesForDate(
                  start,
                  end,
                  lead.id,
                  lead.convertedToEventId,
                  leadEspacios
                );

            return (
              <div
                key={d.id}
                className={`mb-3 rounded-lg border border-zinc-200 dark:border-zinc-700/60 p-3 ${
                  isConverted ? "opacity-60" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-800 dark:text-zinc-200">
                      {formatDateRange(start, end)}
                    </div>
                    {d.label && (
                      <div className="text-xs text-zinc-400 mt-0.5">{d.label}</div>
                    )}
                  </div>
                  {!isConverted && !soloLectura && (
                    <button
                      onClick={() => handleRemoveInterestDate(d.id)}
                      className="p-1 text-zinc-400 hover:text-red-500"
                      title="Eliminar fecha"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {clashes.map((clash, ci) => (
                  <div
                    key={ci}
                    className={`flex items-start gap-1.5 mt-2 text-xs font-medium ${
                      clash.severity === "red" ? "text-red-600" : "text-amber-600"
                    }`}
                  >
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 mt-px" />
                    <span>
                      {clash.type === "event" ? (
                        <>
                          {clash.espacios?.length > 0
                            ? `${clash.espacios.join(", ")} no está disponible`
                            : "Esta fecha no está disponible"}{" "}
                          — {clash.name}
                        </>
                      ) : clash.type === "ocupacion" ? (
                        `${clash.ocupados} de ${clash.total} espacios ocupados — libres: ${clash.libres.join(", ")}`
                      ) : (
                        `También interesados: ${clash.names.join(", ")}`
                      )}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}

          {!isConverted && !soloLectura && (
            <div className="rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50 dark:bg-zinc-800/30 p-3 mt-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-3">
                Agregar fecha
              </div>
              <div className="space-y-3">
                <DateTimeDropdowns
                  label="Inicio bloqueo"
                  value={newDateRange.startDate}
                  onChange={(v) =>
                    setNewDateRange((p) => {
                      const newStart = new Date(v);
                      const curEnd = new Date(p.endDate);
                      // Si el fin quedó atrás, cerrar en el MISMO día (23:59)
                      // en vez de saltar 24h conservando la hora.
                      if (curEnd <= newStart) {
                        return {
                          ...p,
                          startDate: v,
                          endDate: DateTime.fromFormat(v, FMT)
                            .endOf("day")
                            .toFormat(FMT),
                        };
                      }
                      return { ...p, startDate: v };
                    })
                  }
                />
                <DateTimeDropdowns
                  label="Fin bloqueo"
                  value={newDateRange.endDate}
                  onChange={(v) =>
                    setNewDateRange((p) => {
                      const newEnd = new Date(v);
                      const curStart = new Date(p.startDate);
                      // Simétrico: abrir al inicio del MISMO día (00:00).
                      if (newEnd <= curStart) {
                        return {
                          ...p,
                          endDate: v,
                          startDate: DateTime.fromFormat(v, FMT)
                            .startOf("day")
                            .toFormat(FMT),
                        };
                      }
                      return { ...p, endDate: v };
                    })
                  }
                />
                <div>
                  <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                    Etiqueta (opcional)
                  </div>
                  <Input
                    value={newDateRange.label}
                    onChange={(e) =>
                      setNewDateRange((p) => ({ ...p, label: e.target.value }))
                    }
                    placeholder="Ej: Sábado tarde"
                  />
                </div>
              </div>
              <button
                onClick={handleAddInterestDate}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
              >
                <PlusIcon className="h-4 w-4" />
                Agregar fecha
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── TIMELINE TAB ────────────────────────────────────── */}
      {tab === "timeline" && (
        <div>
          {isConverted && (
            <div className="mb-3 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 italic">
              <CheckCircleIcon className="h-4 w-4 text-green-600 shrink-0" />
              Este lead ya es un evento — los nuevos seguimientos se escriben desde el evento.
            </div>
          )}
          <LeadNotasTimeline
            leadId={leadId}
            venueId={lead.venueId}
            leadTopic={lead.topic || ""}
            isViewer={isViewer}
            readOnly={isConverted || soloLectura}
          />
        </div>
      )}

      {/* ── COTIZACIÓN TAB ──────────────────────────────────── */}
      {tab === "cotizacion" && !isViewer && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <DocumentTextIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Cotización
            </span>
          </div>

          <div className="mb-4">
            <CotizadorDesglose
              venueId={selectedVenueId}
              desglose={desglose}
              ivaEnabled={ivaEnabled}
              onIvaChange={saveIvaEnabled}
              onDesgloseChange={(nuevo) => {
                setEditData((prev) => ({ ...prev, desglose: nuevo }));
                saveDesgloseToDB(nuevo);
              }}
              conceptos={conceptosCotizables}
              conceptosTodos={conceptos}
              readOnly={isConverted || soloLectura}
              soloLectura={soloLectura}
              puedeCrearServicio={!isConverted && !soloLectura}
              titulo="Desglose"
            />
          </div>

          {/* Export PDF — no se ofrece en solo lectura (P-FRA-13). */}
          {desglose.length > 0 && !soloLectura && (
            <button
              onClick={handleExportLeadPDF}
              disabled={exportingPDF}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50 mb-4"
              style={{ backgroundColor: ACCENT }}
            >
              <DocumentTextIcon className="h-4 w-4" />
              {exportingPDF ? "Generando..." : "Exportar PDF"}
            </button>
          )}

          {/* Cotización history */}
          {cotizacionHistory.length > 0 && (
            <div className="mb-4">
              <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-3">
                Historial de cotizaciones
              </div>
              <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: ACCENT + "0d" }}>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Folio
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Fecha
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Generada por
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                    {cotizacionHistory.map((entry, idx) => {
                      const genDate = entry.generatedAt?.seconds
                        ? new Date(entry.generatedAt.seconds * 1000).toLocaleDateString(
                            "es-MX",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "-";
                      return (
                        <tr key={idx}>
                          <td className="px-3 py-2 font-medium text-zinc-900 dark:text-white">
                            {entry.folio || `COT-L-${String(idx + 1).padStart(4, "0")}`}
                          </td>
                          <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                            {genDate}
                          </td>
                          <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                            {entry.generatedByName || "-"}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => handleToggleCotizacionSent(idx)}
                              disabled={soloLectura}
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition disabled:cursor-default ${
                                entry.sent
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                                  : "bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400"
                              }`}
                              title={
                                soloLectura
                                  ? undefined
                                  : entry.sent
                                    ? "Clic para marcar como no enviada"
                                    : "Clic para marcar como enviada"
                              }
                            >
                              {entry.sent ? "Enviada" : "No enviada"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Sticky action footer ──────────────────────────────── */}
      {!soloLectura && !isConverted && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-[var(--vb-sb-w,0px)] z-40 border-t border-zinc-200 dark:border-zinc-700/60 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
            {!confirmingDelete ? (
              <>
                <button
                  onClick={() => setConfirmingDelete(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                >
                  <TrashIcon className="h-4 w-4" />
                  Eliminar lead
                </button>
                <div className="flex-1" />
                {interestDates.length > 0 && (
                  <button
                    onClick={() => {
                      const dates = lead?.interestDates || [];
                      let firstAvailable = 0;
                      for (let i = 0; i < dates.length; i++) {
                        const s = dates[i].startDate?.toDate
                          ? dates[i].startDate.toDate()
                          : new Date(dates[i].startDate);
                        const e = dates[i].endDate?.toDate
                          ? dates[i].endDate.toDate()
                          : new Date(dates[i].endDate);
                        const c = getClashesForDate(
                          s,
                          e,
                          lead.id,
                          lead.convertedToEventId,
                          leadEspacios
                        );
                        if (!c.some((cl) => cl.type === "event")) {
                          firstAvailable = i;
                          break;
                        }
                      }
                      setConvertDateIdx(firstAvailable);
                      setConvertCopyDesglose(true);
                      setConvertEspacios(leadEspacios);
                      setShowConvertDialog(true);
                    }}
                    className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                    style={{ backgroundColor: "#16a34a" }}
                  >
                    Convertir a evento
                  </button>
                )}
                <button
                  onClick={handleSaveLead}
                  disabled={savingEdit}
                  className="inline-flex items-center justify-center rounded-lg px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {savingEdit ? "Guardando..." : "Guardar cambios"}
                </button>
              </>
            ) : (
              <div className="w-full space-y-2 rounded-lg border border-red-200 bg-red-50 dark:border-red-800/40 dark:bg-red-900/20 p-3">
                <label className="block text-xs font-medium text-red-700 dark:text-red-300">
                  Razón de eliminación
                </label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="¿Por qué se elimina este lead?"
                  rows={2}
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setConfirmingDelete(false);
                      setDeleteReason("");
                    }}
                    className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteLead}
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    Confirmar eliminación
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Conversion confirmation dialog ─────────────────────── */}
      {showConvertDialog && lead && (() => {
        const dateClashes = interestDates.map((d) => {
          const s = d.startDate?.toDate ? d.startDate.toDate() : new Date(d.startDate);
          const e = d.endDate?.toDate ? d.endDate.toDate() : new Date(d.endDate);
          return getClashesForDate(
            s,
            e,
            lead.id,
            lead.convertedToEventId,
            convertEspacios
          );
        });
        const selectedClashes = dateClashes[convertDateIdx] || [];
        const selectedHasEventClash = selectedClashes.some((c) => c.type === "event");
        return (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowConvertDialog(false)}
            />
            <div className="relative w-full max-w-lg rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900">
              <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
                <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                  Convertir a evento
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                  Se creará un evento a partir de este lead
                </p>
              </div>
              <div className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Sin correo ni teléfono la ficha de cliente que nace de la
                    conversión no tiene con qué ligarse a una existente, y
                    acaba duplicada. Avisar aquí, antes de crear nada. Si el
                    lead ya tiene cliente, el evento se liga a ese y no hay
                    nada que avisar. */}
                {!lead.clienteId && !lead.userEmail?.trim() && !lead.phone?.trim() && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                    <span>
                      Este lead no tiene correo ni teléfono. Al convertirlo no se podrá
                      ligar solo a un cliente que ya esté en el directorio: captúralos antes de
                      convertir, o liga el cliente desde el evento después.
                    </span>
                  </div>
                )}
                {/* Espacios del evento. Última oportunidad de decidirlo: un
                    evento ya creado solo se edita desde el calendario, y sin
                    selección nace bloqueando todo el venue. */}
                {espaciosActivos.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-2">
                      Espacios que ocupará el evento
                    </label>
                    <EspaciosPicker
                      opciones={espaciosActivos}
                      value={convertEspacios}
                      onChange={setConvertEspacios}
                      size="sm"
                    />
                    <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                      {convertEspacios.length === 0
                        ? "Sin espacios seleccionados, el evento bloqueará TODO el venue (calendario y disponibilidad pública)."
                        : "Las fechas de abajo se evalúan contra esta selección."}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-2">
                    {interestDates.length > 1
                      ? "Seleccionar fecha para el evento"
                      : "Fecha del evento"}
                  </label>
                  <div className="space-y-2">
                    {interestDates.map((d, idx) => {
                      const start = d.startDate?.toDate
                        ? d.startDate.toDate()
                        : new Date(d.startDate);
                      const end = d.endDate?.toDate
                        ? d.endDate.toDate()
                        : new Date(d.endDate);
                      const clashes = dateClashes[idx] || [];
                      const hasEventClash = clashes.some((c) => c.type === "event");
                      return (
                        <label
                          key={d.id}
                          className={`flex items-start gap-3 rounded-lg border p-3 transition ${
                            hasEventClash
                              ? "border-red-300 bg-red-50/50 cursor-not-allowed opacity-70"
                              : convertDateIdx === idx
                                ? "border-[#1b4d2e] bg-[#1b4d2e]/5 cursor-pointer"
                                : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 cursor-pointer"
                          }`}
                        >
                          <input
                            type="radio"
                            checked={convertDateIdx === idx}
                            onChange={() => {
                              if (!hasEventClash) setConvertDateIdx(idx);
                            }}
                            disabled={hasEventClash}
                            className="accent-[#1b4d2e] mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div
                              className={`text-sm ${
                                hasEventClash
                                  ? "text-zinc-400 line-through"
                                  : "text-zinc-800 dark:text-zinc-200"
                              }`}
                            >
                              {formatDateRange(start, end)}
                            </div>
                            {d.label && (
                              <div className="text-xs text-zinc-400 mt-0.5">{d.label}</div>
                            )}
                            {clashes.map((clash, ci) => (
                              <div
                                key={ci}
                                className={`flex items-start gap-1.5 mt-1.5 text-xs font-medium ${
                                  clash.type === "event"
                                    ? "text-red-600"
                                    : "text-amber-600"
                                }`}
                              >
                                <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                <div>
                                  {clash.type === "ocupacion" ? (
                                    <div>
                                      {clash.ocupados} de {clash.total} espacios
                                      ocupados — libres: {clash.libres.join(", ")}
                                    </div>
                                  ) : clash.type === "event" ? (
                                    <>
                                      <div>
                                        {clash.espacios?.length > 0
                                          ? `${clash.espacios.join(", ")} no disponible`
                                          : "Fecha no disponible"}{" "}
                                        — {clash.name}
                                      </div>
                                      <div className="text-[11px] text-red-500 font-normal mt-0.5">
                                        Inicio bloqueo:{" "}
                                        {clash.eventStart.toLocaleDateString("es-MX", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          timeZone: "America/Mexico_City",
                                        })}
                                        {" · "}
                                        Fin bloqueo:{" "}
                                        {clash.eventEnd.toLocaleDateString("es-MX", {
                                          day: "numeric",
                                          month: "short",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          timeZone: "America/Mexico_City",
                                        })}
                                      </div>
                                    </>
                                  ) : (
                                    <div>
                                      También interesados: {clash.names.join(", ")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {lead.desglose?.length > 0 && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={convertCopyDesglose}
                      onChange={(e) => setConvertCopyDesglose(e.target.checked)}
                      className="accent-[#1b4d2e] h-4 w-4"
                    />
                    <span className="text-sm text-zinc-700 dark:text-zinc-300">
                      Copiar desglose al evento
                    </span>
                  </label>
                )}
              </div>
              <div className="flex justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
                <button
                  onClick={() => setShowConvertDialog(false)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConvertToEvent}
                  disabled={converting || selectedHasEventClash}
                  className="rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                  style={{
                    backgroundColor: selectedHasEventClash ? "#a1a1aa" : "#16a34a",
                  }}
                  title={
                    selectedHasEventClash
                      ? "La fecha seleccionada tiene un conflicto con un evento existente"
                      : ""
                  }
                >
                  {converting
                    ? "Convirtiendo..."
                    : selectedHasEventClash
                      ? "Fecha no disponible"
                      : "Confirmar conversión"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LeadPage;
